/**
 * @file SubmissionFormReadOnly.tsx
 * @description
 * Renders the submission data in a read-only, locked form UI. This is used when the submission
 * has reached a stage (like IRB_REVIEW or DECISION_ISSUED) where the user can no longer edit the data.
 *
 * Key Features:
 * - Fetches the submission by query param "submissionId"
 * - Displays each question answer as text
 * - Displays document fields (lists of uploaded files) as links or file names (no removal)
 *
 * @dependencies
 * - React, React Router
 * - docStorageService for generating public file URLs (optional).
 *
 * @notes
 * - This finalizes the "locked read-only" portion of Step 5. The user sees the submission data but cannot edit.
 * - The actual form fields displayed are read from submission.data. For clarity, we do a simple text mapping.
 *   If you want to replicate the exact structure of `FormSubmissionFields`, you can do so, but this
 *   simpler approach is enough for demonstration.
 */

import { DocumentDownloadLink } from '../components/DocumentDownloadLink';
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSupabase } from '../contexts/SupabaseContext';
import UserNavBar from '../components/UserNavBar';
import { useAuth } from '../hooks/useAuth';
import { FormField } from '../components/FormSubmissionFields';
import { resolveFormSchemaConfig } from '../forms/formSchemaConfig';
import { Document } from '../lib/supabaseClient';
import SharedReadOnlyBanner from '../components/submissions/SharedReadOnlyBanner';
import withIdleTimer from '../hoc/withIdleTimer';

interface DocumentMetadata extends Document {
  // Any additional properties not covered by Document
}

interface Submission {
  id: string;
  user_id: string;
  status: string;
  data: Record<string, any>;
  created_at: string;
  updated_at: string;
  payment_completed?: boolean;
  submission_type?: string;
}

/**
 * Safely renders HTML content for field labels
 * @param htmlContent The HTML string to render
 * @returns React element with the parsed HTML
 */
const renderFromHtml = (htmlContent: string) => {
  return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
};

/**
 * SubmissionFormReadOnly
 * -----------------------
 * Displays a locked read-only version of submission data. Users can't edit or remove documents.
 */
const SubmissionFormReadOnly: React.FC = () => {
  const [searchParams] = useSearchParams();
  const submissionId = searchParams.get('submissionId');
  const navigate = useNavigate();
  const { user, signOut, loading: authLoading, sessionExpired } = useAuth();

  const [submission, setSubmission] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(true);
  const [documentsMap, setDocumentsMap] = useState<Record<string, DocumentMetadata[]>>({});
  
  // For section navigation
  const [activeSection, setActiveSection] = useState(0);
  
  // Add ref to track if a fetch is in progress
  const isFetchingRef = useRef(false);
  // Add ref to track the current user ID
  const userIdRef = useRef<string | null>(null);
  
  // Add state for document URLs
  
  // Create a memoized Supabase client (similar to NeedsActionView)
  const supabase = useSupabase();
  
  // Resolve the schema by the loaded submission's type. Before the submission
  // loads this defaults to the initial schema; for NEW_SUBMISSION/AMENDMENT it
  // stays the initial schema (same array reference), for CONTINUING_REVIEW it
  // switches to the CR schema once loaded.
  const resolvedSections = useMemo(
    () => resolveFormSchemaConfig(submission?.submission_type).sections,
    [submission]
  );

  // Initialize section refs (kept in sync with the resolved schema length).
  const sectionRefs = useMemo(() => {
    return resolvedSections.map(() => React.createRef<HTMLDivElement>());
  }, [resolvedSections]);

  // Add effect to handle sessionExpired
  useEffect(() => {
    if (sessionExpired) {
      navigate('/signin');
    }
  }, [sessionExpired, navigate]);

  // Memoize the fetchData function
  const fetchData = useCallback(async () => {
    // Skip if already fetching
    if (isFetchingRef.current) {
      return;
    }
    
    // Skip if no submission ID or user
    if (!submissionId || !user) {
      setLoading(false);
      return;
    }
    
    isFetchingRef.current = true;
    setLoading(true);

    try {
      // 1) Fetch submission record
      const { data: subData, error: subError } = await supabase
        .from('submissions')
        .select('*')
        .eq('id', submissionId)
        .single();

      if (subError || !subData) {
        console.error('Error fetching submission:', subError);
        setSubmission(null);
        return;
      }
      setSubmission(subData as Submission);

      // 2) Fetch active documents for this submission
      const { data: docsData, error: docsError } = await supabase
        .from('documents')
        .select('*')
        .eq('submission_id', submissionId)
        .eq('is_active', true);

      if (docsError) {
        console.error('Error fetching documents for read-only view:', docsError);
      } else {
        const docMap: Record<string, DocumentMetadata[]> = {};
        (docsData || []).forEach((doc: Document) => {
          const fieldName = doc.type || ''; // Add null check with default empty string
          if (!docMap[fieldName]) docMap[fieldName] = [];
          docMap[fieldName].push(doc as DocumentMetadata);
        });
        setDocumentsMap(docMap);
      }
    } catch (err) {
      console.error('Unexpected error fetching read-only data:', err);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [submissionId, supabase, user]);

  // Update the main data fetch effect to respect auth loading
  useEffect(() => {
    // Update userIdRef when user changes
    if (user?.id !== userIdRef.current) {
      userIdRef.current = user?.id || null;
    }
    
    // Skip if auth is still loading
    if (authLoading) {
      return;
    }

    fetchData();
  }, [submissionId, authLoading, user, fetchData]);

  // Add effect to generate signed URLs for documents


  // Function to scroll to a specific section
  const scrollToSection = (index: number) => {
    if (sectionRefs[index]?.current) {
      sectionRefs[index].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(index);
    }
  };

  // Handle user logout
  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };



  // Add auth loading state UI
  if (authLoading) {
    return (
      <div className="flex flex-col h-screen">
        <UserNavBar user={user} onSignOut={handleLogout} />
        <div className="flex-1 pt-20 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F55D3E] mx-auto mb-4"></div>
            <p className="text-gray-600">Verifying your account...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!submissionId) {
    return (
      <div className="p-8">
        <h1 className="text-xl font-bold mb-4">No Submission Selected</h1>
        <p className="text-gray-500">
          No <code>submissionId</code> query parameter found. Unable to load any data.
        </p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-[#F55D3E] text-white rounded"
        >
          Go Back
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8">
        <h1 className="text-xl font-bold mb-4">Loading submission data...</h1>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="p-8">
        <h1 className="text-xl font-bold mb-4">Submission not found</h1>
        <p className="text-gray-500">
          We couldn't find a submission with the specified ID, or you don't have access to it.
        </p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-[#F55D3E] text-white rounded"
        >
          Go Back
        </button>
      </div>
    );
  }

  const isSharedReadOnly = !!user && submission.user_id !== user.id;

  return (
    <div className="flex flex-col h-screen">
      {/* User Navigation Bar */}
      <UserNavBar user={user} onSignOut={handleLogout} />

      <div className="flex flex-1 overflow-hidden pt-20">
        {/* Fixed Sidebar for Section Navigation */}
        <div className="w-64 bg-white shadow-md border-r border-gray-200 overflow-y-auto">
          <div className="p-4">
            <h2 className="text-lg font-bold text-[#343a40] mb-4">Form Sections</h2>
            <ul className="space-y-1">
              {resolvedSections.map((section, idx) => (
                <li key={`section-${idx}`}>
                  <button
                    onClick={() => scrollToSection(idx)}
                    className={`w-full text-left py-2 px-3 rounded text-sm transition-colors ${
                      activeSection === idx
                        ? 'bg-[#F55D3E] text-white font-medium'
                        : 'hover:bg-gray-100 text-[#343a40]'
                    }`}
                  >
                    {section.heading}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto p-6 relative">
          <div className="max-w-4xl mx-auto">
            {/* Submission Header */}
            <div className="mb-8 pb-4 border-b border-gray-200">
              <h1 className="text-2xl font-bold text-[#343a40]">
                Submission Form (Locked View)
              </h1>
              <p className="text-gray-600">
                Last Updated: {new Date(submission.updated_at).toLocaleString()}
              </p>
            </div>

            {isSharedReadOnly && <SharedReadOnlyBanner className="mb-6" />}

            {/* Form Sections */}
            {resolvedSections.map((section, sectionIndex) => {
              // First filter the section items to only those with values
              const itemsWithValues = section.items.filter((item: FormField) => {
                // Skip if no name or not a display field type
                if (!item.name || ['button'].includes(item.itemType as string)) return false;
                
                // For document fields, check if documents exist
                if (item.itemType === 'document' && item.name) {
                  const docs = documentsMap[item.name] || [];
                  return docs.length > 0;
                }
                
                // For regular fields, check if value exists and is not empty
                if (item.name && submission.data) {
                  const fieldValue = submission.data[item.name];
                  if (fieldValue === undefined || fieldValue === null || fieldValue === '') return false;
                  if (Array.isArray(fieldValue) && fieldValue.length === 0) return false;
                  return true;
                }
                
                return false;
              });
              
              // Only render section if it has items with values
              if (itemsWithValues.length === 0) return null;
              
              return (
                <div 
                  key={`section-${sectionIndex}`} 
                  ref={sectionRefs[sectionIndex]}
                  className="mb-10 scroll-mt-4"
                >
                  <h2 className="text-xl font-semibold text-[#343a40] mb-4 pb-2 border-b border-gray-200">
                    {section.heading}
                  </h2>
                  <div className="space-y-6">
                    {itemsWithValues.map((item: FormField, itemIndex) => {
                      // Document field
                      if (item.itemType === 'document' && item.name) {
                        const fieldName = item.name;
                        const docs = documentsMap[fieldName] || [];
                        
                        return (
                          <div key={`item-${sectionIndex}-${itemIndex}`} className="border border-gray-200 rounded-md p-4">
                            <h3 className="font-medium text-[#343a40] mb-2">
                              {typeof item.label === 'string' ? renderFromHtml(item.label) : item.label}
                            </h3>
                            <ul className="space-y-2">
                              {docs.map((doc: DocumentMetadata) => (
                                <li key={doc.id} className="flex items-center">
                                  <svg 
                                    className="w-5 h-5 text-gray-600 mr-2" 
                                    fill="currentColor" 
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                                  </svg>
                                  <DocumentDownloadLink document={doc} client={supabase} />
                                  <span className="text-xs text-gray-500 ml-2">
                                    (Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()})
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      }
                      
                      // Regular form fields (text, textarea, checkbox, etc.)
                      if (item.name) {
                        const fieldValue = submission.data[item.name];
                        return (
                          <div key={`item-${sectionIndex}-${itemIndex}`} className="border border-gray-200 rounded-md p-4">
                            <h3 className="font-medium text-[#343a40] mb-2">
                              {typeof item.label === 'string' ? renderFromHtml(item.label) : item.label}
                            </h3>
                            <div className="mt-1 bg-gray-50 p-3 rounded">
                              {typeof fieldValue === 'boolean' ? (
                                <span>{fieldValue ? 'Yes' : 'No'}</span>
                              ) : Array.isArray(fieldValue) ? (
                                <ul className="list-disc list-inside">
                                  {fieldValue.map((val, i) => (
                                    <li key={i} className="text-gray-800">{val}</li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-gray-800 whitespace-pre-line">{fieldValue.toString()}</p>
                              )}
                            </div>
                          </div>
                        );
                      }
                      
                      return null; // Should never reach here, but needed for TypeScript
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default withIdleTimer(SubmissionFormReadOnly);
